import type { BookLanguage, FlashcardCollection, FlashcardNoteSource } from "@japa-reader/contracts";
import {
  ArrowLeft,
  Check,
  Eye,
  FolderPlus,
  Layers3,
  LoaderCircle,
  PenLine,
  Plus,
  Sparkles,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  BASIC_NOTE_TYPE_ID,
  loadFlashcardCollection,
  newFlashcardDeck,
  newFlashcardNote,
  reconcileNoteCards,
  saveFlashcardDeck,
  saveFlashcardNote,
} from "../lib/flashcards";
import { deckPath } from "../lib/flashcard-search";
import { generateVocabularyFlashcardDraft } from "../lib/library";

const PREFERRED_DECK_KEY = "japa-reader-vocabulary-deck";

export type VocabularyFlashcardContext = {
  term: string;
  reading: string;
  meaning: string;
  sentence: string;
  language: BookLanguage;
  tags: string[];
  source?: FlashcardNoteSource;
};

type DraftMode = "ai" | "manual";
type DialogStep = "loading" | "deck" | "method" | "generating" | "draft" | "success";

type VocabularyFlashcardDialogProps = {
  context: VocabularyFlashcardContext;
  onClose: () => void;
};

export function VocabularyFlashcardDialog({ context, onClose }: VocabularyFlashcardDialogProps) {
  const [collection, setCollection] = useState<FlashcardCollection | null>(null);
  const [selectedDeckId, setSelectedDeckId] = useState("");
  const [newDeckName, setNewDeckName] = useState("");
  const [step, setStep] = useState<DialogStep>("loading");
  const [mode, setMode] = useState<DraftMode>("ai");
  const [front, setFront] = useState("");
  const [back, setBack] = useState("");
  const [draftView, setDraftView] = useState<"preview" | "edit">("preview");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const standardDecks = useMemo(
    () => collection?.decks.filter((deck) => deck.kind === "standard") ?? [],
    [collection],
  );
  const selectedDeck = standardDecks.find((deck) => deck.id === selectedDeckId) ?? null;

  useEffect(() => {
    let active = true;
    void loadFlashcardCollection()
      .then((loaded) => {
        if (!active) return;
        setCollection(loaded);
        const preferredId = window.localStorage.getItem(PREFERRED_DECK_KEY) || "";
        const preferred = loaded.decks.find((deck) => deck.id === preferredId && deck.kind === "standard");
        if (preferred) {
          setSelectedDeckId(preferred.id);
          setStep("method");
        } else {
          const firstDeck = loaded.decks.find((deck) => deck.kind === "standard");
          setSelectedDeckId(firstDeck?.id || "");
          setStep("deck");
        }
      })
      .catch((loadError) => {
        if (!active) return;
        setError(loadError instanceof Error ? loadError.message : "Não foi possível abrir seus baralhos.");
        setStep("deck");
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, saving]);

  function confirmDeck() {
    if (!selectedDeckId) {
      setError("Escolha um baralho para continuar.");
      return;
    }
    window.localStorage.setItem(PREFERRED_DECK_KEY, selectedDeckId);
    setError("");
    setStep("method");
  }

  async function createDeck() {
    const name = newDeckName.trim();
    if (!name || !collection) return;
    setSaving(true);
    setError("");
    try {
      const deck = newFlashcardDeck(name);
      await saveFlashcardDeck(deck);
      setCollection({ ...collection, decks: [...collection.decks, deck] });
      setSelectedDeckId(deck.id);
      setNewDeckName("");
      window.localStorage.setItem(PREFERRED_DECK_KEY, deck.id);
      setStep("method");
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : "Não foi possível criar o baralho.");
    } finally {
      setSaving(false);
    }
  }

  function beginManualDraft() {
    setMode("manual");
    setFront(context.sentence);
    setBack(manualBackTemplate(context));
    setDraftView("edit");
    setError("");
    setStep("draft");
  }

  async function generateWithAi(returnTo: DialogStep = "method") {
    if (!collection) return;
    setMode("ai");
    setError("");
    setStep("generating");
    try {
      const draft = await generateVocabularyFlashcardDraft({
        term: context.term,
        reading: context.reading,
        meaning: context.meaning,
        sourceSentence: context.sentence,
        language: context.language,
        avoidPatterns: recentGeneratedFronts(collection),
      });
      setFront(draft.front);
      setBack(draft.back);
      setDraftView("preview");
      setStep("draft");
    } catch (generationError) {
      setError(generationError instanceof Error ? generationError.message : "Não foi possível gerar o flashcard.");
      setStep(returnTo);
    }
  }

  async function saveDraft() {
    if (!collection || !selectedDeckId || !front.trim() || !back.trim()) {
      setError("Preencha a frente e o verso antes de criar o flashcard.");
      return;
    }
    const noteType = collection.noteTypes.find((candidate) => candidate.id === BASIC_NOTE_TYPE_ID);
    if (!noteType) {
      setError("O tipo de nota básico não está disponível.");
      return;
    }
    const firstField = [...noteType.fields].sort((left, right) => left.ordinal - right.ordinal)[0];
    const secondField = [...noteType.fields].sort((left, right) => left.ordinal - right.ordinal)[1];
    if (!firstField || !secondField) {
      setError("O tipo de nota básico está incompleto.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const note = newFlashcardNote(selectedDeckId, noteType.id);
      note.fields = {
        [firstField.name]: plainTextToFlashcardHtml(front),
        [secondField.name]: plainTextToFlashcardHtml(back),
      };
      note.tags = [...new Set([...context.tags, "vocabulario", mode === "ai" ? "gerado-por-ia" : "criado-manualmente"])];
      note.source = context.source;
      const reconciled = reconcileNoteCards(note, noteType, []);
      if (!reconciled.cards.length) throw new Error("O cartão não pôde ser montado.");
      await saveFlashcardNote(note, reconciled.cards);
      setCollection({
        ...collection,
        notes: [...collection.notes, note],
        cards: [...collection.cards, ...reconciled.cards],
      });
      setStep("success");
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Não foi possível salvar o flashcard.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="modal-backdrop flash-modal-backdrop vocabulary-flashcard-backdrop"
      role="presentation"
      onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) onClose(); }}
    >
      <section className={`modal-card vocabulary-flashcard-dialog is-${step}`} role="dialog" aria-modal="true" aria-labelledby="vocabulary-flashcard-title">
        <button className="modal-close" onClick={onClose} disabled={saving} aria-label="Fechar"><X size={20} /></button>
        <div className="vocabulary-dialog-header">
          <span className="eyebrow">FLASHCARD DE VOCABULÁRIO</span>
          <h2 id="vocabulary-flashcard-title">Criar com <span lang={context.language}>{context.term}</span></h2>
          <div className="vocabulary-selected-word">
            <div><strong lang={context.language}>{context.term}</strong><span>{context.reading || "Sem leitura informada"}</span></div>
            <p>{context.meaning}</p>
          </div>
        </div>

        {step === "loading" && <LoadingState label="Abrindo seus baralhos…" />}

        {step === "deck" && (
          <div className="vocabulary-flow-panel">
            <div className="vocabulary-flow-heading"><span>1 de 3</span><div><h3>Em qual baralho?</h3><p>Vamos lembrar sua escolha para as próximas palavras. Você poderá trocá-la quando quiser.</p></div></div>
            <div className="vocabulary-deck-list" role="radiogroup" aria-label="Escolher baralho">
              {standardDecks.map((deck) => (
                <button
                  type="button"
                  role="radio"
                  aria-checked={deck.id === selectedDeckId}
                  className={`vocabulary-deck-option ${deck.id === selectedDeckId ? "is-selected" : ""}`}
                  key={deck.id}
                  onClick={() => { setSelectedDeckId(deck.id); setError(""); }}
                >
                  <Layers3 size={18} /><span><strong>{deckPath(deck, collection?.decks ?? [])}</strong><small>{collection?.notes.filter((note) => note.deckId === deck.id).length ?? 0} notas</small></span>
                  <i>{deck.id === selectedDeckId && <Check size={14} />}</i>
                </button>
              ))}
            </div>
            <div className="vocabulary-new-deck">
              <div><FolderPlus size={17} /><span><strong>Novo baralho</strong><small>Crie um sem sair desta tela</small></span></div>
              <div><input value={newDeckName} onChange={(event) => setNewDeckName(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void createDeck(); }} placeholder="Ex.: Vocabulário do livro" aria-label="Nome do novo baralho" /><button type="button" onClick={() => void createDeck()} disabled={!newDeckName.trim() || saving}><Plus size={16} /> Criar</button></div>
            </div>
            <div className="modal-actions"><button className="ghost-button" onClick={onClose}>Cancelar</button><button className="align-button" onClick={confirmDeck} disabled={!selectedDeckId}>Continuar</button></div>
          </div>
        )}

        {step === "method" && (
          <div className="vocabulary-flow-panel">
            <div className="vocabulary-flow-heading"><span>2 de 3</span><div><h3>Como você quer criar?</h3><p>O cartão será adicionado a <strong>{selectedDeck ? deckPath(selectedDeck, collection?.decks ?? []) : "seu baralho"}</strong>.</p></div></div>
            <div className="vocabulary-method-grid">
              <button type="button" className="vocabulary-method-card is-ai" onClick={() => void generateWithAi("method")}>
                <Sparkles size={24} /><span><strong>Gerar com IA</strong><small>MiniMax M3 cria uma frase inédita e o verso completo.</small></span><b>Recomendado</b>
              </button>
              <button type="button" className="vocabulary-method-card" onClick={beginManualDraft}>
                <PenLine size={24} /><span><strong>Criar manualmente</strong><small>Comece com o trecho atual e edite tudo antes de salvar.</small></span>
              </button>
            </div>
            <button type="button" className="vocabulary-change-deck" onClick={() => { setError(""); setStep("deck"); }}><ArrowLeft size={14} /> Trocar baralho</button>
          </div>
        )}

        {step === "generating" && (
          <div className="vocabulary-generating" role="status">
            <div><Sparkles size={21} /><LoaderCircle className="spin" size={34} /></div>
            <h3>Criando uma frase só sua…</h3>
            <p>O M3 está variando o cenário e a estrutura gramatical, depois monta a tradução, a explicação e o vocabulário palavra por palavra.</p>
          </div>
        )}

        {step === "draft" && (
          <div className="vocabulary-review-shell">
            <div className="vocabulary-review-scroll">
              <div className="vocabulary-review-heading">
                <div className="vocabulary-flow-heading"><span>3 de 3</span><div><h3>Revise antes de salvar</h3><p>Nada será adicionado até você aprovar.</p></div></div>
                <span className="vocabulary-review-deck"><Layers3 size={14} /> {selectedDeck ? deckPath(selectedDeck, collection?.decks ?? []) : "Baralho"}</span>
              </div>
              <div className="vocabulary-review-tabs" role="tablist" aria-label="Modo de revisão do flashcard">
                <button type="button" role="tab" aria-selected={draftView === "preview"} className={draftView === "preview" ? "is-active" : ""} onClick={() => setDraftView("preview")}><Eye size={15} /> Prévia</button>
                <button type="button" role="tab" aria-selected={draftView === "edit"} className={draftView === "edit" ? "is-active" : ""} onClick={() => setDraftView("edit")}><PenLine size={15} /> Editar conteúdo</button>
              </div>

              {draftView === "preview" ? (
                <div className="vocabulary-preview-grid">
                  <section className="vocabulary-preview-side is-front">
                    <header><span>FRENTE</span><small>Frase de estudo</small></header>
                    <div className="vocabulary-preview-content" lang={context.language}>{front || "A frente aparecerá aqui."}</div>
                  </section>
                  <section className="vocabulary-preview-side is-back">
                    <header><span>VERSO</span><small>Resposta e explicação</small></header>
                    <div className="vocabulary-preview-content">
                      {splitFlashcardBack(back).map((part, index) => (
                        <section key={`${part.label}-${index}`}>
                          {part.label && <strong>{part.label}</strong>}
                          <p>{part.content || "—"}</p>
                        </section>
                      ))}
                    </div>
                  </section>
                </div>
              ) : (
                <div className="vocabulary-draft-fields">
                  <label><span>Frente <small>Uma frase no idioma estudado</small></span><textarea lang={context.language} value={front} onChange={(event) => setFront(event.target.value)} rows={5} /></label>
                  <label><span>Verso <small>Tradução, explicação e palavras</small></span><textarea value={back} onChange={(event) => setBack(event.target.value)} rows={14} /></label>
                </div>
              )}

              {error && <div className="form-error vocabulary-flashcard-error" role="alert">{error}</div>}
            </div>
            <div className="vocabulary-review-actions">
              <button className="ghost-button" onClick={() => { setError(""); setStep("method"); }} disabled={saving}><ArrowLeft size={15} /> Voltar</button>
              {mode === "ai" && <button className="ghost-button" onClick={() => void generateWithAi("draft")} disabled={saving}><Sparkles size={15} /> Gerar outra</button>}
              <button className="align-button" onClick={() => void saveDraft()} disabled={saving || !front.trim() || !back.trim()}>{saving ? <LoaderCircle className="spin" size={17} /> : <Check size={17} />} Criar 1 flashcard</button>
            </div>
          </div>
        )}

        {step === "success" && (
          <div className="vocabulary-success">
            <span><Check size={27} /></span>
            <h3>Flashcard criado</h3>
            <p><strong lang={context.language}>{context.term}</strong> foi adicionado a <strong>{selectedDeck ? deckPath(selectedDeck, collection?.decks ?? []) : "seu baralho"}</strong>.</p>
            <button className="align-button" onClick={onClose}>Concluir</button>
          </div>
        )}

        {error && step !== "draft" && <div className="form-error vocabulary-flashcard-error" role="alert">{error}</div>}
      </section>
    </div>
  );
}

function LoadingState({ label }: { label: string }) {
  return <div className="vocabulary-generating" role="status"><LoaderCircle className="spin" size={30} /><p>{label}</p></div>;
}

function manualBackTemplate(context: VocabularyFlashcardContext) {
  const vocabulary = context.language === "ja"
    ? `${context.term} (${context.reading || "leitura"}): ${context.meaning} - `
    : `${context.term} (${context.reading || "pronúncia"}): ${context.meaning} - `;
  return `Tradução: \n\nExplicação: \n\nPalavra por palavra:\n${vocabulary}`;
}

function splitFlashcardBack(value: string) {
  const normalized = value.trim();
  if (!normalized) return [{ label: "", content: "" }];
  const marker = /(?:^|\n\s*)(Tradução|Explicação|Palavra por palavra)\s*:\s*/gi;
  const matches = [...normalized.matchAll(marker)];
  if (!matches.length) return [{ label: "", content: normalized }];
  const parts: Array<{ label: string; content: string }> = [];
  const prefix = normalized.slice(0, matches[0].index).trim();
  if (prefix) parts.push({ label: "", content: prefix });
  for (let index = 0; index < matches.length; index += 1) {
    const current = matches[index];
    const contentStart = (current.index ?? 0) + current[0].length;
    const contentEnd = matches[index + 1]?.index ?? normalized.length;
    parts.push({ label: `${current[1]}:`, content: normalized.slice(contentStart, contentEnd).trim() });
  }
  return parts;
}

function recentGeneratedFronts(collection: FlashcardCollection) {
  const basicType = collection.noteTypes.find((noteType) => noteType.id === BASIC_NOTE_TYPE_ID);
  const firstField = [...(basicType?.fields ?? [])].sort((left, right) => left.ordinal - right.ordinal)[0];
  if (!firstField) return [];
  return [...collection.notes]
    .filter((note) => note.tags.includes("gerado-por-ia"))
    .sort((left, right) => right.createdAt - left.createdAt)
    .map((note) => plainTextFromFlashcardHtml(note.fields[firstField.name] || ""))
    .filter(Boolean)
    .slice(0, 10);
}

function plainTextToFlashcardHtml(value: string) {
  return value.trim().replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;").replace(/\r?\n/g, "<br>");
}

function plainTextFromFlashcardHtml(value: string) {
  return value.replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]*>/g, " ").replace(/&nbsp;/gi, " ").replace(/&amp;/gi, "&").replace(/&lt;/gi, "<").replace(/&gt;/gi, ">").replace(/&quot;/gi, '"').replace(/&#0?39;/gi, "'").replace(/\s+/g, " ").trim();
}
